import { type ReactNode } from 'react';

import { t } from '../../../lib/i18n';
import { type User, VerificationStatus } from '../types';
import { isUserVerified } from '../user-verification';
import { cn } from '../utils';

/**
 * The mono uppercase pills of `Redesign-D-Admin{,-Mobile}`.
 *
 * Every status on the admin side is drawn the same way — a 10px monospace
 * caption on a tinted ground — so the operator reads state by colour and never
 * by icon. One component, three tints, no per-screen variants.
 */

const CHIP_BASE =
  'inline-flex w-fit items-center rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] leading-[12px] tracking-[0.08em] uppercase lg:px-2 lg:py-1 lg:text-[10px] lg:leading-[13px]';

export type ChipTone = 'neutral' | 'warn' | 'ok' | 'brand' | 'danger';

const TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'bg-cf-fill text-cf-ink-muted',
  warn: 'bg-cf-warn-tint text-cf-warn',
  ok: 'bg-cf-success-tint text-cf-success',
  brand: 'bg-cf-brand-tint text-cf-brand',
  danger: 'bg-cf-danger-tint text-cf-danger',
};

export function Chip({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: ChipTone;
  className?: string;
  children: ReactNode;
}) {
  return <span className={cn(CHIP_BASE, TONE_CLASS[tone], className)}>{children}</span>;
}

/** Shared class for a chip that is also a button (the verification toggle). */
export const CHIP_BUTTON_CLASS = CHIP_BASE;
export { TONE_CLASS as CHIP_TONE_CLASS };

/** Which of the three verification states a client is in. */
export function verificationTone(user: User): { tone: ChipTone; label: string } {
  if (isUserVerified(user)) return { tone: 'ok', label: t('clients.status.verified') };

  const status = user.verificationStatus;
  if (status === VerificationStatus.IN_REVIEW || status === VerificationStatus.PENDING) {
    // «На перевірці» is only honest once the client has actually submitted
    // something; a bare PENDING on an empty profile is just "not started".
    const submitted = (user.documents?.length ?? 0) > 0 || !!user.lastDocumentUpload;
    if (status === VerificationStatus.IN_REVIEW || submitted) {
      return { tone: 'warn', label: t('clients.status.pending') };
    }
  }
  return { tone: 'neutral', label: t('clients.status.unverified') };
}

/** Read-only verification pill (the list's fallback and the detail header). */
export function VerificationChip({ user, className }: { user: User; className?: string }) {
  const { tone, label } = verificationTone(user);
  return (
    <Chip tone={tone} className={className}>
      {label}
    </Chip>
  );
}
